import type { PaginatedResponse } from "@playbookai/types";
import { apiCall, type ApiClientConfig } from "@playbookai/api-clients";
import type { KpiApiClient } from "./playbook-be-client.js";
import type {
  AssignOwnerInput,
  CreateKpiInput,
  KpiCategoryOption,
  KpiDefinition,
  ManagedKpiObservation,
  ManagedKpiObservationBatch,
  KpiOwnerOption,
  KpiTargetRecord,
  ListKpiCategoryOptionsParams,
  ListKpiDefinitionsParams,
  ListKpiObservationsParams,
  ListKpiOwnerOptionsParams,
  ListKpisParams,
  ManagedKpi,
  ManagedKpiDetail,
  ManagedKpiSummary,
  RecordKpiObservationInput,
  RecordKpiObservationsBatchInput,
  SetKpiTargetInput,
  UpdateKpiInput,
} from "./types.js";

/**
 * Live playbook-be client for the KPI Manager (PB-94), over the shared `apiCall`
 * (`@playbookai/api-clients`): BFF cookies + CSRF, `PaginatedResponse<T>`
 * envelopes, and a typed `PlaybookApiError` on any non-2xx. Reads target
 * `/v1/kpis` + `/v1/kpi-definitions` (PB-93); writes target the governance
 * endpoints on `/v1/kpis/:id/*` (PB-92).
 */
const KPIS_PATH = "/v1/kpis";
const DEFINITIONS_PATH = "/v1/kpi-definitions";

// The Type picker needs the full catalog, so `listDefinitions` follows the
// cursor. A bounded cap guards against an unbounded loop on a bad server cursor;
// hitting it throws rather than silently returning a partial catalog.
const MAX_DEFINITION_PAGES = 20;

// Encode an id as a single path segment — a route-provided id could carry a
// `/`, `?`, or `#` that would otherwise reshape the URL.
const seg = (id: string) => encodeURIComponent(id);

export const kpiApi: KpiApiClient = {
  listKpis(
    config: ApiClientConfig,
    params: ListKpisParams = {},
  ): Promise<PaginatedResponse<ManagedKpiSummary>> {
    return apiCall<PaginatedResponse<ManagedKpiSummary>>(
      config,
      "GET",
      KPIS_PATH,
      undefined,
      {
        q: params.q,
        lifecycleStatus: params.lifecycleStatus,
        definitionId: params.definitionId,
        // Query strings are text; send the flag only when true (an absent `mine`
        // lists every KPI). `buildApiUrl` drops undefined/empty values.
        mine: params.mine ? "true" : undefined,
        // Repeated `ownerUserIds=<uuid>` keys (PB-2941); an empty array is
        // dropped like undefined.
        ownerUserIds: params.ownerUserIds,
        limit: params.limit,
        cursor: params.cursor,
      },
    );
  },

  getKpi(config: ApiClientConfig, kpiId: string): Promise<ManagedKpiDetail> {
    return apiCall<ManagedKpiDetail>(
      config,
      "GET",
      `${KPIS_PATH}/${seg(kpiId)}`,
    );
  },

  listOwnerOptions(
    config: ApiClientConfig,
    params: ListKpiOwnerOptionsParams = {},
  ): Promise<PaginatedResponse<KpiOwnerOption>> {
    return apiCall<PaginatedResponse<KpiOwnerOption>>(
      config,
      "GET",
      `${KPIS_PATH}/owner-options`,
      undefined,
      { limit: params.limit, cursor: params.cursor },
    );
  },

  listCategoryOptions(
    config: ApiClientConfig,
    params: ListKpiCategoryOptionsParams = {},
  ): Promise<PaginatedResponse<KpiCategoryOption>> {
    return apiCall<PaginatedResponse<KpiCategoryOption>>(
      config,
      "GET",
      `${KPIS_PATH}/category-options`,
      undefined,
      { limit: params.limit, cursor: params.cursor },
    );
  },

  listDefinitionPage(
    config: ApiClientConfig,
    params: ListKpiDefinitionsParams = {},
  ): Promise<PaginatedResponse<KpiDefinition>> {
    return apiCall<PaginatedResponse<KpiDefinition>>(
      config,
      "GET",
      DEFINITIONS_PATH,
      undefined,
      { limit: params.limit, cursor: params.cursor },
    );
  },

  async listDefinitions(config: ApiClientConfig): Promise<KpiDefinition[]> {
    const definitions: KpiDefinition[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_DEFINITION_PAGES; page++) {
      const res = await apiCall<PaginatedResponse<KpiDefinition>>(
        config,
        "GET",
        DEFINITIONS_PATH,
        undefined,
        cursor ? { cursor } : undefined,
      );
      definitions.push(...res.data);
      if (!res.hasMore) return definitions;
      // `hasMore` with no cursor is a malformed page — surface it rather than
      // silently returning a partial catalog the picker would treat as complete.
      if (!res.nextCursor) {
        throw new Error(
          "KPI Type catalog pagination indicated more results without a next cursor.",
        );
      }
      cursor = res.nextCursor;
    }
    throw new Error(
      `KPI Type catalog pagination exceeded ${MAX_DEFINITION_PAGES} pages; refusing to return a partial catalog.`,
    );
  },

  getDefinition(
    config: ApiClientConfig,
    definitionId: string,
  ): Promise<KpiDefinition> {
    return apiCall<KpiDefinition>(
      config,
      "GET",
      `${DEFINITIONS_PATH}/${seg(definitionId)}`,
    );
  },

  createKpi(
    config: ApiClientConfig,
    input: CreateKpiInput,
  ): Promise<ManagedKpi> {
    return apiCall<ManagedKpi>(config, "POST", KPIS_PATH, input);
  },

  updateKpi(
    config: ApiClientConfig,
    kpiId: string,
    input: UpdateKpiInput,
  ): Promise<ManagedKpi> {
    return apiCall<ManagedKpi>(
      config,
      "PATCH",
      `${KPIS_PATH}/${seg(kpiId)}`,
      input,
    );
  },

  assignOwner(
    config: ApiClientConfig,
    kpiId: string,
    input: AssignOwnerInput,
  ): Promise<ManagedKpi> {
    return apiCall<ManagedKpi>(
      config,
      "PATCH",
      `${KPIS_PATH}/${seg(kpiId)}/owner`,
      input,
    );
  },

  setTarget(
    config: ApiClientConfig,
    kpiId: string,
    input: SetKpiTargetInput,
  ): Promise<KpiTargetRecord> {
    return apiCall<KpiTargetRecord>(
      config,
      "POST",
      `${KPIS_PATH}/${seg(kpiId)}/targets`,
      input,
    );
  },

  activate(config: ApiClientConfig, kpiId: string): Promise<ManagedKpi> {
    return apiCall<ManagedKpi>(
      config,
      "POST",
      `${KPIS_PATH}/${seg(kpiId)}/activate`,
    );
  },

  archive(config: ApiClientConfig, kpiId: string): Promise<ManagedKpi> {
    return apiCall<ManagedKpi>(
      config,
      "POST",
      `${KPIS_PATH}/${seg(kpiId)}/archive`,
    );
  },

  restore(config: ApiClientConfig, kpiId: string): Promise<ManagedKpi> {
    return apiCall<ManagedKpi>(
      config,
      "POST",
      `${KPIS_PATH}/${seg(kpiId)}/restore`,
    );
  },

  listObservations(
    config: ApiClientConfig,
    kpiId: string,
    params: ListKpiObservationsParams = {},
    // TanStack's per-query AbortSignal, threaded through every page of the
    // range sweep — without it an unmounted chart's remaining pages kept
    // downloading.
    signal?: AbortSignal,
  ): Promise<PaginatedResponse<ManagedKpiObservation>> {
    return apiCall<PaginatedResponse<ManagedKpiObservation>>(
      config,
      "GET",
      `${KPIS_PATH}/${seg(kpiId)}/observations`,
      undefined,
      {
        from: params.from,
        to: params.to,
        limit: params.limit,
        cursor: params.cursor,
      },
      signal,
    );
  },

  recordObservation(
    config: ApiClientConfig,
    kpiId: string,
    input: RecordKpiObservationInput,
  ): Promise<ManagedKpiObservation> {
    return apiCall<ManagedKpiObservation>(
      config,
      "POST",
      `${KPIS_PATH}/${seg(kpiId)}/observations`,
      input,
    );
  },

  recordObservations(
    config: ApiClientConfig,
    kpiId: string,
    input: RecordKpiObservationsBatchInput,
  ): Promise<ManagedKpiObservationBatch> {
    return apiCall<ManagedKpiObservationBatch>(
      config,
      "POST",
      `${KPIS_PATH}/${seg(kpiId)}/observations/batch`,
      input,
    );
  },
};
